import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { PHONE_1, PHONE_1_TEL, PHONE_2, PHONE_2_TEL, EMAIL, EMAIL_2, ADDRESS, MAP_EMBED, MAP_LINK } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent! Our team will reach out shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your enquiry. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-slate-50 border-t border-slate-200">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-greenDark" data-testid="contact-overline">Contact Us</p>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight text-slate-900" data-testid="contact-heading">
            Let's Talk <span className="font-serif italic font-semibold text-brand-blue">Supplies</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Wholesale pricing, bulk orders or a monthly supply contract — send us a message and
            we'll get back within one business day.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-10">
          <Reveal>
            <form onSubmit={submit} className="rounded-[2rem] border border-slate-200 bg-white p-8 md:p-10 shadow-[0_30px_60px_-40px_rgba(18,58,102,0.3)]" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Your Name" id="name">
                  <input required minLength={2} value={form.name} onChange={set("name")} data-testid="contact-name-input" placeholder="Rahul Deshmukh" className={inputCls} />
                </Field>
                <Field label="Phone" id="phone">
                  <input required minLength={8} value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" placeholder="+91 98XXX XXXXX" className={inputCls} />
                </Field>
              </div>
              <Field label="Email" id="email" className="mt-5">
                <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" placeholder="you@company.com" className={inputCls} />
              </Field>
              <Field label="Message" id="message" className="mt-5">
                <textarea required minLength={5} rows={4} value={form.message} onChange={set("message")} data-testid="contact-message-input" placeholder="Tell us what you need — products, quantities, delivery city…" className={`${inputCls} resize-none`} />
              </Field>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-blue py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-brand-blueDark hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Sending…" : "Send Enquiry"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="flex h-full flex-col gap-6">
              <div className="grid sm:grid-cols-2 gap-5" data-testid="contact-details">
                <Detail icon={<Phone size={18} />} label="Call Us">
                  <a href={PHONE_1_TEL} className="block font-bold text-slate-900 hover:text-brand-blue" data-testid="contact-phone-1">{PHONE_1}</a>
                  <a href={PHONE_2_TEL} className="block font-bold text-slate-900 hover:text-brand-blue" data-testid="contact-phone-2">{PHONE_2}</a>
                </Detail>
                <Detail icon={<Mail size={18} />} label="Email">
                  <a href={`mailto:${EMAIL}`} className="block font-bold text-slate-900 hover:text-brand-blue break-all" data-testid="contact-email-1">{EMAIL}</a>
                  <a href={`mailto:${EMAIL_2}`} className="block font-bold text-slate-900 hover:text-brand-blue" data-testid="contact-email-2">{EMAIL_2}</a>
                </Detail>
                <Detail icon={<MapPin size={18} />} label="Visit Us">
                  <a href={MAP_LINK} target="_blank" rel="noopener noreferrer" className="font-bold text-slate-900 hover:text-brand-blue" data-testid="contact-address">{ADDRESS}</a>
                </Detail>
                <Detail icon={<Clock size={18} />} label="Business Hours">
                  <p className="font-bold text-slate-900" data-testid="contact-hours">Mon – Sat</p>
                  <p className="text-sm text-slate-500">9:30 AM – 7:00 PM</p>
                </Detail>
              </div>
              <div className="flex-1 overflow-hidden rounded-[2rem] border border-slate-200 min-h-[280px]">
                <iframe
                  title="Sevayog Facilities location map"
                  src={MAP_EMBED}
                  className="h-full min-h-[280px] w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors duration-200 focus:border-brand-blue focus:bg-white focus:ring-2 focus:ring-brand-blue/15";

function Field({ label, id, children, className = "" }) {
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function Detail({ icon, label, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-2.5 text-brand-blue">
        {icon}
        <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</span>
      </div>
      <div className="mt-3 text-sm">{children}</div>
    </div>
  );
}
